export type YearInBand = {
  yearId: string;
  yearName: string;
  typicalAgeMinYears?: number | null;
  typicalAgeMaxYears?: number | null;
};

export type EducationSystemKeyStage = {
  name: string;
  keyStageId?: string;
  /** Stable year id + display name; sole source of years for this band. */
  years: YearInBand[];
};

export type EducationSystemDefinition = {
  key: string;
  displayName: string;
  isBuiltIn: boolean;
  keyStages: EducationSystemKeyStage[];
  /** Tenant custom only: a subject uses this system key. */
  systemKeyInUse?: boolean;
  /** Tenant custom: (key stage id, year id) pairs with topics in the student curriculum. */
  placementsInUse?: PlacementInUse[];
};

export type PlacementInUse = { keyStageId: string; yearId: string };

/** Maps E313v1_GotEducationSystemDefinitions payload (either casing) to a stable shape. */
export function parseEducationSystemDefinitionsEvent(e: unknown): EducationSystemDefinition[] {
  const ex = e as Record<string, unknown> | null | undefined;
  const raw = (ex?.e313_Definitions ?? ex?.E313_Definitions) as unknown[] | undefined;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row: any) => {
      const keyStagesRaw = row?.e313_KeyStages ?? row?.E313_KeyStages ?? [];
      const placementsRaw = row?.e313_PlacementsInUse ?? row?.E313_PlacementsInUse ?? [];
      const placementsInUse: PlacementInUse[] = Array.isArray(placementsRaw)
        ? (placementsRaw as any[])
            .map((p) => {
              const ksId =
                p?.e313_PlacementKeyStageId ??
                p?.E313_PlacementKeyStageId ??
                p?.e313_KeyStageId ??
                p?.E313_KeyStageId;
              const yId =
                p?.e313_PlacementYearId ??
                p?.E313_PlacementYearId ??
                p?.e313_YearId ??
                p?.E313_YearId;
              return {
                keyStageId: ksId != null && String(ksId).trim() !== '' ? String(ksId) : '',
                yearId: yId != null && String(yId).trim() !== '' ? String(yId) : ''
              };
            })
            .filter((p) => p.keyStageId && p.yearId)
        : [];
      const skiu = row?.e313_SystemKeyInUse ?? row?.E313_SystemKeyInUse;
      return {
        key: String(row?.e313_Key ?? row?.E313_Key ?? ''),
        displayName: String(row?.e313_DisplayName ?? row?.E313_DisplayName ?? ''),
        isBuiltIn: Boolean(row?.e313_IsBuiltIn ?? row?.E313_IsBuiltIn),
        keyStages: (keyStagesRaw as any[]).map((ks) => {
          const name = String(ks?.e313_Name ?? ks?.E313_Name ?? '');
          const yearsFromBand = (ks?.e313_Years ?? ks?.E313_Years ?? []) as any[];
          const years: YearInBand[] = Array.isArray(yearsFromBand)
            ? yearsFromBand
                .map((y) => {
                  const yName = String(y?.e313_YearName ?? y?.E313_YearName ?? '').trim();
                  const idRaw = y?.e313_YearId ?? y?.E313_YearId;
                  const yearId = idRaw != null && String(idRaw).trim() !== '' ? String(idRaw) : '';
                  if (!yearId || !yName) return null;
                  const minRaw = y?.e313_TypicalAgeMinYears ?? y?.E313_TypicalAgeMinYears;
                  const maxRaw = y?.e313_TypicalAgeMaxYears ?? y?.E313_TypicalAgeMaxYears;
                  const typicalAgeMinYears =
                    minRaw != null && minRaw !== '' && !Number.isNaN(Number(minRaw))
                      ? Number(minRaw)
                      : null;
                  const typicalAgeMaxYears =
                    maxRaw != null && maxRaw !== '' && !Number.isNaN(Number(maxRaw))
                      ? Number(maxRaw)
                      : null;
                  return {
                    yearId,
                    yearName: yName,
                    typicalAgeMinYears,
                    typicalAgeMaxYears
                  } as YearInBand;
                })
                .filter((x): x is YearInBand => x != null)
            : [];
          const ksIdRaw = ks?.e313_KeyStageId ?? ks?.E313_KeyStageId;
          const keyStageId =
            ksIdRaw != null && String(ksIdRaw).trim() !== '' ? String(ksIdRaw) : undefined;
          return {
            name,
            years,
            keyStageId
          } satisfies EducationSystemKeyStage;
        }),
        systemKeyInUse: skiu === true || skiu === 'true',
        placementsInUse: placementsInUse.length ? placementsInUse : undefined
      };
    })
    .filter((d) => d.key);
}

/** First registered built-in framework (E313 is ordered); use when a profile key is missing and the UI has loaded definitions. */
export function firstBuiltInEducationSystemKey(defs: EducationSystemDefinition[]): string {
  const built = defs.filter((d) => d.isBuiltIn && d.key).sort((a, b) => a.key.localeCompare(b.key));
  return built[0]?.key ?? '';
}

export function getDefinitionForKey(defs: EducationSystemDefinition[], key: string): EducationSystemDefinition | undefined {
  return defs.find((d) => d.key === key);
}

/** Years under a key stage band identified by stable keyStageId from E313. */
export function getYearsForKeyStageId(
  def: EducationSystemDefinition | undefined,
  keyStageId: string
): YearInBand[] {
  if (!def?.keyStages?.length) return [];
  const want = keyStageId.trim();
  if (!want) return [];
  const ks = def.keyStages.find((k) => (k.keyStageId ?? '').trim() === want);
  return ks?.years ?? [];
}

/** True if yearId appears under keyStageId in this education system definition. */
export function isPlacementIdsValidForDefinition(
  def: EducationSystemDefinition | undefined,
  keyStageId: string,
  yearId: string
): boolean {
  const yid = yearId.trim();
  if (!keyStageId.trim() || !yid) return false;
  return getYearsForKeyStageId(def, keyStageId).some((y) => y.yearId === yid);
}
